import React, { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Divider from '@mui/material/Divider';
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import TextBox from "@refere/ui-utils/ui/components/TextBox"
import ListBox from "@refere/ui-utils/ui/components/ListBox"
import CheckBox from "@refere/ui-utils/ui/components/CheckBox"
import FormModifierWrapper from "@refere/ui-utils/ui/components/FormModifierWrapper"
import DatePickerBox from "@refere/ui-utils/ui/components/DatePickerBox"
import { ModalDialog, ModalDialogHeader } from "@refere/ui-utils/ui/components/ModalDialog"

import differenceInYears from 'date-fns/differenceInYears';
import { useHookstate } from "@hookstate/core";
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from "react-hook-form";
import { date, object, string } from 'yup';
import useMediaQuery from "@mui/material/useMediaQuery";

import { somenteNumeros } from "@refere/ui-utils/ui/utils/format";
import { isEmailValido } from "@refere/ui-utils/ui/utils/validations"

import { globalState } from "../../../../features/global-state";
import { IPassoContratacaoState, getEnderecoPorCep, savePassoContratacao } from "../../../../features/pages/seguro/passo-contracao";


const schema = object({
    quatroUltimosDigitosCartao: string()
        .nullable().trim()
        .required("Os 4 últimos números do cartão é obrigatório")
        .length(4, "Deve informar os últimos ${length} número do cartão"),
    codTpCartao: string().nullable()
        .required("Deve ser escolhido um tipo de cartão"),
    codSexo: string().nullable()
        .required("Escolha uma opção de sexo"),
    dtNascimento: date().nullable()
        .required("Data de nascimento é obrigatório")
        .max(new Date(), 'Não é possível incluir uma data futura')
        .test('idade', 'Este produto não está disponível para a idade informada', (value) => {
            const idade = differenceInYears(new Date(), new Date(value));
            return idade >= 16 && idade <= 75
        }),
    codEstadoCivil: string().nullable()
        .required("Deve ser escolhido uma opção de estado civil"),
    email: string().nullable()
        .required("E-mail é obrigatório")
        .test("is-valid", "E-mail inválido", (value) => 
            isEmailValido(value)
        )
        .max(100, "E-mail deve ter no máximo ${max} caracteres"),
    celular: string().nullable()
        .required("Celular é obrigatório")
        .transform(value => somenteNumeros(value))
        .min(10, "Celular deve ter no mínimo ${min} caracteres")
        .max(20, "Celular deve ter no máximo ${max} caracteres"),
    cep: string().nullable()
        .required("CEP é obrigatório")
        .transform(value => somenteNumeros(value))
        .length(8, "Cep inválido"),
    logradouro: string().nullable()
        .required("Logradouro é obrigatório")
        .max(200, "CEP deve ter no máximo ${max} caracteres"),
    complemento: string().nullable()
        .max(200, "Complemento deve ter no máximo ${max} caracteres"),
    bairro: string().nullable()
        .required("Bairro é obrigatório")
        .max(200, "Bairro deve ter no máximo ${max} caracteres"),
    cidade: string().nullable()
        .required("Cidade é obrigatório")
        .max(200, "Cidade deve ter no máximo ${max} caracteres"),
    siglaUf: string().nullable()
        .required("UF é obrigatório"),
    nr: string()         
        .when('semNr', ([semNr], schema) => {
            if (!semNr || semNr === null) 
                return schema.required('Número é obrigatório');
        }) 
});

const PassoContratacao = () =>{
    const passoContratacaoState = useHookstate(globalState.passoContracao);
    const [ modalIsOpen, setModalIsOpen ] = useState(false);

    const { control, setValue, clearErrors, getValues, watch, trigger, handleSubmit } = useForm<IPassoContratacaoState>({
        mode: "onBlur",
        resolver: yupResolver(schema as any)
    });

    useEffect(() => {
        const passoContratacao = passoContratacaoState.get({ noproxy: true });
        if (passoContratacao) {
            for(const field in passoContratacao) {
                setValue(field as any, passoContratacao[field]);
            }
            setValue("ultimoCepPesquisado" as any, somenteNumeros(passoContratacao.cep));
        }
    }, [ passoContratacaoState ]);

    useEffect(() => {
        const cepSomenteNumeros = somenteNumeros(getValues("cep"));
        if (cepSomenteNumeros.length === 8 && getValues("ultimoCepPesquisado" as any) !== cepSomenteNumeros) {
            setValue("ultimoCepPesquisado" as any, cepSomenteNumeros);

            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            handleSearchCep();
        }
    }, [ watch("cep") ]);

    const handleSearchCep = async () => {
        
        const cepSoNumeros = somenteNumeros(getValues("cep"));

        if(!cepSoNumeros || cepSoNumeros.length < 8)
            return;

        const response = await getEnderecoPorCep(cepSoNumeros);
        if (response) {
            setValue("logradouro", response?.logradouro);
            setValue("bairro", response?.bairro);
            setValue("cidade", response?.cidade);
            setValue("siglaUf", response?.uf);
            
            clearErrors('logradouro');
            clearErrors('bairro');
            clearErrors('cidade');
            clearErrors('siglaUf');
        }
    }

    const tiposCartoes = passoContratacaoState?.tiposCartoes?.get({noproxy: true}).map(item => ({
        value: item.codTpCartao, 
        label: item.nome
    }))

    const sexos = passoContratacaoState?.sexos?.get({noproxy: true}).map(item => ({
        value: item.codSexo, 
        label: item.nome
    }))

    const estadosCivis = passoContratacaoState?.estadosCivis?.get({noproxy: true}).map(item => ({
        value: item.codEstadoCivil, 
        label: item.nome
    }))

    const ufs = passoContratacaoState?.ufs?.get({noproxy: true}).map(item => ({
        value: item.siglaUf, 
        label: item.siglaUf
    }))

    const match600 = useMediaQuery("(max-width: 600px)");
    const match500 = useMediaQuery("(max-width: 500px)");

    return(
        <>
        {
            passoContratacaoState?.get() &&
            <form onSubmit={
                async(e) => {
                    e.preventDefault();

                    await handleSubmit(() => {
                        setModalIsOpen(true);
                    })();
                }
            }>
                <Stack direction="column">
                    <Box>
                        <Box>
                            <Typography fontSize="16px">
                                Cartão segurado
                            </Typography>
                            <Divider />
                        </Box>
                        <Box mt={2} display="flex" flexWrap="wrap" alignItems="flex-start">
                            <Box width={match500 ? "100%" : "220px"} mt={4.375} mr={match500 ? 0 : 1}>
                                <FormModifierWrapper 
                                    name="quatroUltimosDigitosCartao"
                                    control={control}
                                    render={({ field, fieldState }) => 
                                        <TextBox 
                                            { ...field }                                        
                                            label="4 últimos dígitos do cartão"
                                            value={field.value}
                                            mask="9999" 
                                            error={fieldState.invalid}
                                        />
                                    }
                                />
                            </Box>
                            <Box width={match500 ? "100%" : "200px"}>
                                <FormModifierWrapper name="codTpCartao" control={control} render={
                                    ({ field, fieldState }) => 
                                        <ListBox
                                            {...field}
                                            label="Tipo Cartão"
                                            options={tiposCartoes}
                                            value={field.value}
                                            error={fieldState.invalid}
                                            size="medium" 
                                        />
                                    }
                                />
                            </Box>
                        </Box>
                        <Box mt={1}>
                            <Typography fontSize="16px">
                                Dados complementares
                            </Typography>
                            <Divider />
                        </Box>
                        <Box mt={2} display="flex" flexWrap="wrap" alignItems={match500 ? "flex-end" : "flex-start"}>
                            <Box width={match500 ? "100%" : "220px"} mr={match500 ? 0 : 1}>
                                <FormModifierWrapper 
                                    name="codSexo"
                                    control={control}
                                    render={({ field, fieldState }) => 
                                        <ListBox
                                            {...field}
                                            label="Sexo"
                                            options={sexos}
                                            value={field.value}
                                            error={fieldState.invalid}
                                            size="medium"                                    
                                        />
                                    }
                                />
                            </Box>
                            <Box mt={4.375} width={match500 ? "100%" : "190px"}>
                                <FormModifierWrapper 
                                    name="dtNascimento"
                                    control={control}
                                    render={({ field, fieldState }) => 
                                        <DatePickerBox 
                                            { ...field }
                                            label="Nascimento"
                                            value={field.value}
                                            error={fieldState.invalid}
                                        />
                                    }
                                />
                            </Box>
                        </Box>
                        <Box mt={1} maxWidth="500px" display="flex" flexDirection="column" >
                            <FormModifierWrapper 
                                name="codEstadoCivil"
                                control={control}
                                render={({ field, fieldState }) => 
                                    <ListBox
                                        {...field}
                                        label="Estado Civil"
                                        options={estadosCivis}
                                        value={field.value}
                                        error={fieldState.invalid}
                                        size="medium"  
                                        orientation={match600 ? "vertical" :"horizontal"}   
                                        helperText={passoContratacaoState?.estadoCivilHelperText.get()}
                                    />
                                }
                            />   
                        </Box>
                        <Box mt={1} display="flex" flexDirection={match500 ? "column" : "row"}>
                            <Box width={match500 ? "100%" : `calc(100% - 160px)`} mr={match500 ? 0 : 1}>
                                <FormModifierWrapper 
                                    name="email"
                                    control={control}
                                    render={({ field, fieldState }) => 
                                        <TextBox 
                                            { ...field }
                                            label="E-mail"
                                            value={field.value}
                                            error={fieldState.invalid}                                                                              
                                        />
                                    }
                                />
                            </Box>
                            <Box width={match500 ? "100%" : "160px"} mt={match500 ? 1 : 0}>
                                <FormModifierWrapper 
                                    name="celular"
                                    control={control}
                                    render={({ field, fieldState }) => 
                                        <TextBox 
                                            { ...field }
                                            label="Celular"
                                            value={field.value}
                                            mask="(99) 99999-9999"
                                            error={fieldState.invalid}                                                                              
                                        />
                                    }
                                />
                            </Box>
                        </Box>
                        <Box width="100%" mt={1} display="flex" flexDirection={match600 ? "column" : "row"}>
                            <Box width={match500 ? "100%" : "120px"} mb={1} mr={match500 ? 0 : 1}>
                                <FormModifierWrapper 
                                    name="cep"
                                    control={control}
                                    render={({ field, fieldState }) => 
                                        <TextBox 
                                            { ...field }
                                            label="CEP"
                                            value={field.value}
                                            mask="99999-999" 
                                            error={fieldState.invalid}
                                        />
                                    }
                                />
                            </Box>  
                            <Box mt={match500 ? 1 : 0} width={match600 ? "100%" : `calc(100% - 128px)`} display="flex" flexDirection={match500 ? "column" : "row"} >                                                               
                                <Box width={match500 ? "100%" : "200px"} display="flex" flexDirection="row">                                                               
                                    <Box width={match500 ? `calc(100% - 100px)` : "100px"}>
                                        <FormModifierWrapper 
                                            name="nr"
                                            control={control}
                                            render={({ field, fieldState }) => 
                                                <TextBox 
                                                    { ...field}
                                                    label="Nº"
                                                    value={watch("semNr") ? null : field.value}
                                                    error={fieldState.invalid} 
                                                    disabled={watch("semNr") ?? false} 
                                                />
                                            }
                                        />
                                    </Box>
                                    <Box width="100px" display="flex" flexDirection="row" textAlign="left" alignItems="center" height="40px">
                                       <FormModifierWrapper 
                                            name="semNr"
                                            control={control}
                                            render={({ field }) => 
                                                <CheckBox size="small" label="semNr" {...field} onChange={ async (e: any) => {
                                                if (e.target.checked) {
                                                    setValue("semNr", true);
                                                    setValue("nr", "");
                                                }
                                                else 
                                                    setValue("semNr", false)
                                                
                                                await trigger("nr");
                                            }} />
                                            }
                                        /> 
                                    </Box>  
                                </Box>                      
                                <Box width={match500 ? "100%" : `calc(100% - 200px)`} mt={match500 ? 1 : 0}>
                                    <FormModifierWrapper 
                                        name="complemento"
                                        control={control}
                                        render={({ field, fieldState }) => 
                                            <TextBox 
                                                { ...field }
                                                label="Complemento"
                                                value={field.value}
                                                error={fieldState.invalid}                                                                               
                                            />
                                        }
                                    />
                                </Box>
                            </Box>
                        </Box>  
                        <Box mt={1} display="flex" flexDirection={match500 ? "column" : "row"}>
                            <Box width={match500 ? "100%" : "50%"} mr={match500 ? 0 : 1} >
                                <FormModifierWrapper 
                                    name="logradouro"
                                    control={control}
                                    render={({ field, fieldState }) => 
                                        <TextBox 
                                            { ...field }
                                            label="Logradouro"
                                            value={field.value}
                                            error={fieldState.invalid}                                                                               
                                        />
                                    }
                                />
                            </Box>
                            <Box width={match500 ? "100%" : "50%"} mt={match500 ? 1 : 0}>
                                <FormModifierWrapper 
                                    name="bairro"
                                    control={control}
                                    render={({ field, fieldState }) => 
                                        <TextBox 
                                            { ...field }
                                            label="Bairro"
                                            value={field.value}
                                            error={fieldState.invalid}                                                                               
                                        />
                                    }
                                />
                            </Box>
                        </Box>
                        <Box mt={1} display="flex" flexDirection="row" alignSelf="center" >
                            <Box width={`calc(100% - 80px)`} mr={1}>
                                <FormModifierWrapper 
                                    name="cidade"
                                    control={control}
                                    render={({ field, fieldState }) => 
                                        <TextBox 
                                            { ...field }
                                            label="Cidade"
                                            value={field.value}
                                            error={fieldState.invalid}                                                                               
                                        />
                                    }
                                />
                            </Box>
                            <Box width="80px" display="flex" flexDirection="column" >
                                <FormModifierWrapper 
                                    name="siglaUf"
                                    control={control}
                                    render={({ field, fieldState }) => 
                                        <ListBox
                                            {...field}
                                            label="UF"
                                            options={ufs}
                                            value={field.value}
                                            error={fieldState.invalid}
                                            size="small"  
                                            variant="select"
                                        />
                                    }
                                />   
                            </Box>
                        </Box>
                    </Box>
                </Stack>
                <Box sx={{ float: "right" }}>
                    <Button 
                        sx={{
                            mt: 2,
                            textTransform: "none",
                            minWidth: "200px"
                        }}
                        variant="contained"
                        type="submit" >
                        Contratar
                    </Button>
                    <ModalDialog 
                        onClose={() => setModalIsOpen(false)} 
                        isOpen={modalIsOpen}
                        sx={{
                            ".MuiPaper-root": {
                                maxWidth: "none",
                                width: "735px"
                            }
                        }} >
                        <ModalDialogHeader
                            onClose={() => setModalIsOpen(false)} >
                            <Box minWidth="200px">
                                Declaração
                            </Box>
                        </ModalDialogHeader>
                        <DialogContent dividers>
                            <p style={{ marginTop: 0}}>
                                Declaro que dei ciência ao proponente de todas as características do seguro que está sendo 
                                contratado e que a ele foi dada a possibilidade de leitura de leitura prévia dos documentos da 
                                seguradora, tal como Manual do Produto e Condições Gerais.
                            </p>
                            <p>
                                Confirmo também que todas as informações prestradas sobre o proponente s sobre o risco 
                                coberto estão de acordo com as informações que recebi do mesmo.
                            </p>
                        </DialogContent> 
                        <DialogActions>
                            <Button 
                                variant='outlined' 
                                sx={{ textTransform: "none" }} 
                                onClick={() => setModalIsOpen(false)} >
                                Cancelar
                            </Button>
                            <Button 
                                variant="contained" 
                                sx={{ textTransform: "none" }} 
                                onClick={async () => {
                                    await handleSubmit(async (validvalue: IPassoContratacaoState) => {
                                        await savePassoContratacao(validvalue);
                                    })();
                                }} >
                                Ok, seguir para a contratação.
                            </Button>
                        </DialogActions>
                    </ModalDialog>
                </Box>
            </form>
        }
        </>
    )
}

export default PassoContratacao